import { toAlgoliaHighlightTokens } from './utils/highlightTokens';

type TypesenseDefaultsItem = {
  query: string;
  label: string;
  highlight?: Record<string, unknown>;
  _highlightResult: {
    query: {
      value: string;
      matchLevel: 'none';
      matchedWords: string[];
    };
  };
};

function normalizeResultsPayload(data: any): TypesenseDefaultsItem[] {
  const results = Array.isArray(data?.results) ? data.results : [];
  const items: TypesenseDefaultsItem[] = [];

  results.forEach((result: any) => {
    const collection =
      result?.request_params?.collection_name ?? result?.request_params?.collection ?? '';

    if (collection !== 'suggestions') {
      return;
    }

    (result?.hits ?? []).forEach((hit: any) => {
      const label = hit?.document?.q ?? '';
      if (!label) {
        return;
      }

      items.push({
        query: label,
        label,
        highlight: hit?.highlight ?? {},
        _highlightResult: {
          query: {
            value: toAlgoliaHighlightTokens(hit?.highlight?.q?.snippet || label),
            matchLevel: 'none',
            matchedWords: [],
          },
        },
      });
    });
  });

  return items;
}

function buildCacheKey(endpoint: string, lang: string) {
  return `typesense_defaults_cache:${endpoint}:${lang}`;
}

function isCacheFresh(cacheKey: string, ttlSeconds: number) {
  try {
    const raw = window.localStorage.getItem(cacheKey);
    if (!raw) {
      return false;
    }

    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed?.savedAt ?? 0);

    return savedAt > 0 && Date.now() - savedAt <= ttlSeconds * 1000;
  } catch (_error) {
    return false;
  }
}

function writeDefaultsCache(cacheKey: string, items: TypesenseDefaultsItem[], limit: number) {
  try {
    window.localStorage.setItem(
      cacheKey,
      JSON.stringify({
        savedAt: Date.now(),
        items: items.slice(0, limit),
      })
    );
  } catch (_error) {
  }
}

function readConfigFromAutocompleteContainer() {
  const container = document.querySelector('[data-ca-typesense-autocomplete]') as HTMLElement | null;
  if (!container) {
    return null;
  }

  const endpoint = String(container.getAttribute('data-ca-typesense-defaults-endpoint') || '');
  const lang = String(container.getAttribute('data-ca-typesense-defaults-lang') || '').trim() || 'en';
  const ttlSeconds = Math.max(
    Number(container.getAttribute('data-ca-typesense-defaults-ttl') || '3600') || 3600,
    0
  );

  return {
    endpoint,
    lang,
    ttlSeconds,
    perPage: 10,
  };
}

function runDefaultsPrefetch() {
  const config = readConfigFromAutocompleteContainer();
  const endpoint = String(config?.endpoint || '');
  const lang = String(config?.lang || '').trim() || 'en';
  const ttlSeconds = Math.max(Number(config?.ttlSeconds ?? 3600) || 3600, 0);
  const perPage = Math.max(Number(config?.perPage ?? 10) || 10, 1);

  if (!endpoint || ttlSeconds <= 0) {
    return;
  }

  const cacheKey = buildCacheKey(endpoint, lang);
  if (isCacheFresh(cacheKey, ttlSeconds)) {
    return;
  }

  let requestUrl: URL;
  try {
    requestUrl = new URL(endpoint, window.location.origin);
    requestUrl.searchParams.set('q', '*');
    requestUrl.searchParams.set('per_page', String(perPage));
  } catch (_error) {
    return;
  }

  fetch(requestUrl.toString())
    .then((response) => {
      if (!response.ok) {
        throw new Error('Bad response');
      }
      return response.json();
    })
    .then((data) => {
      writeDefaultsCache(cacheKey, normalizeResultsPayload(data), perPage);
    })
    .catch(() => {
    });
}

export function scheduleDefaultsPrefetch() {
  if (typeof window === 'undefined') {
    return;
  }

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(runDefaultsPrefetch, { timeout: 1200 });
    return;
  }

  setTimeout(runDefaultsPrefetch, 0);
}
